import { useCallback, useEffect, useRef, useState } from "react";

const THUMB_MIN_H = 52.05;

function useCustomScrollbar(textareaRef, attachKey) {
  const [hasOverflow, setHasOverflow] = useState(false);
  const hasOverflowRef = useRef(false);
  const trackRef = useRef(null);
  const thumbRef = useRef(null);
  const thumbHRef = useRef(THUMB_MIN_H);
  const thumbTopRef = useRef(0);
  const rafRef = useRef(null);

  const update = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const el = textareaRef.current;
      const track = trackRef.current;
      if (!el || !track) return;
      const trackH = track.clientHeight;
      const { scrollTop, scrollHeight, clientHeight } = el;
      const overflow = scrollHeight > clientHeight;
      if (hasOverflowRef.current !== overflow) {
        hasOverflowRef.current = overflow;
        setHasOverflow(overflow);
      }
      const ratio = clientHeight / scrollHeight;
      const h = Math.max(THUMB_MIN_H, ratio * trackH);
      thumbHRef.current = h;
      const maxThumbTop = trackH - h;
      const top =
        scrollHeight === clientHeight
          ? 0
          : (scrollTop / (scrollHeight - clientHeight)) * maxThumbTop;
      thumbTopRef.current = top;

      if (thumbRef.current) {
        thumbRef.current.style.top = `${top}px`;
        thumbRef.current.style.height = `${h}px`;
      }
    });
  }, [textareaRef]);

  // Re-attaches listeners every time attachKey changes (e.g. tab switch).
  // Falls back to a MutationObserver if the element isn't in DOM yet.
  useEffect(() => {
    let bodyObserver = null;
    let elCleanup = null;

    const attach = () => {
      const el = textareaRef.current;
      if (!el) return false;

      el.addEventListener("scroll", update, { passive: true });
      el.addEventListener("input", update, { passive: true });
      update();
      const mo = new MutationObserver(update);
      mo.observe(el, { childList: true, subtree: true, characterData: true });
      const ro = new ResizeObserver(update);
      ro.observe(el);

      elCleanup = () => {
        el.removeEventListener("scroll", update);
        el.removeEventListener("input", update);
        mo.disconnect();
        ro.disconnect();
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
      };
      return true;
    };

    if (!attach()) {
      bodyObserver = new MutationObserver(() => {
        if (attach()) { bodyObserver.disconnect(); bodyObserver = null; }
      });
      bodyObserver.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      bodyObserver?.disconnect();
      elCleanup?.();
    };
    // attachKey forces this effect to re-run on tab switch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textareaRef, update, attachKey]);

  // Also update when track becomes visible
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const ro = new ResizeObserver(() => update());
    ro.observe(track);
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) update();
    }, { threshold: 0 });
    io.observe(track);
    return () => { ro.disconnect(); io.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [update, attachKey]);

  const beginDrag = useCallback(
    (startY, startTop) => {
      const onMove = (ev) => {
        const el = textareaRef.current;
        const track = trackRef.current;
        if (!el || !track) return;
        const trackH = track.clientHeight;
        const delta = ev.clientY - startY;
        const maxThumbTop = trackH - thumbHRef.current;
        const newTop = Math.min(Math.max(0, startTop + delta), maxThumbTop);
        thumbTopRef.current = newTop;
        if (thumbRef.current) {
          thumbRef.current.style.top = `${newTop}px`;
        }
        const { scrollHeight, clientHeight } = el;
        el.scrollTop =
          maxThumbTop > 0
            ? (newTop / maxThumbTop) * (scrollHeight - clientHeight)
            : 0;
      };

      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [textareaRef]
  );

  // Only fires when clicking the track background (not the thumb)
  const onTrackPointerDown = useCallback(
    (e) => {
      if (thumbRef.current && thumbRef.current.contains(e.target)) return;

      e.preventDefault();
      const el = textareaRef.current;
      const track = trackRef.current;
      if (!el || !track) return;

      const trackH = track.clientHeight;
      const rect = track.getBoundingClientRect();
      const clickY = e.clientY - rect.top;
      const maxThumbTop = trackH - thumbHRef.current;
      const newTop = Math.min(Math.max(0, clickY - thumbHRef.current / 2), maxThumbTop);
      thumbTopRef.current = newTop;
      if (thumbRef.current) {
        thumbRef.current.style.top = `${newTop}px`;
      }

      const { scrollHeight, clientHeight } = el;
      el.scrollTop =
        maxThumbTop > 0
          ? (newTop / maxThumbTop) * (scrollHeight - clientHeight)
          : 0;

      beginDrag(e.clientY, newTop);
    },
    [textareaRef, beginDrag]
  );

  const onThumbPointerDown = useCallback(
    (e) => {
      e.preventDefault();
      beginDrag(e.clientY, thumbTopRef.current);
    },
    [beginDrag]
  );

  return {
    thumbTop: thumbTopRef.current,
    thumbH: thumbHRef.current,
    hasOverflow,
    trackRef,
    thumbRef,
    onTrackPointerDown,
    onThumbPointerDown,
    update,
  };
}

export default useCustomScrollbar;
